import { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, DatePicker, TimePicker, Select, Row, Col, Card, Statistic, Typography } from 'antd';
import type { TableProps } from 'antd';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
  fetchVehicleAvailability,
  summarizeVehicleAvailability,
  formatAvailabilityStatusLabel,
  getAvailabilityStatusColor,
  formatTime,
  formatCapacity,
  useSupabaseSession,
  parseUserRole,
  isAdminRole,
  isFleetOwnerRole,
  useFleetOperator,
  usePortalPermissions,
} from '@ubus/shared';
import type { VehicleAvailabilityRow, VehicleAvailabilityStatus } from '@ubus/shared';
import AdminListPage from '../components/AdminListPage';
import AdminSectionPage from '../components/AdminSectionPage';
import StatusTag from '../components/StatusTag';
import { DEFAULT_TABLE_PAGINATION } from '../utils/tableConfig';
import ForbiddenPage from './ForbiddenPage';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

function parseDateParam(raw: string | null): Dayjs {
  if (raw && dayjs(raw, 'YYYY-MM-DD', true).isValid()) {
    return dayjs(raw, 'YYYY-MM-DD');
  }
  return dayjs();
}

function FleetAvailability() {
  const { t } = useTranslation();
  const { session, loading: authLoading } = useSupabaseSession();
  const user = session?.user ?? null;
  const isHqAdmin = isAdminRole(parseUserRole(user));
  const isFleetOwner = isFleetOwnerRole(parseUserRole(user));
  const { operatorId, loading: fleetLoading } = useFleetOperator();
  const { hasPermission, loading: permLoading } = usePortalPermissions();
  const canView = hasPermission('fleet_availability.view');
  const canAccessPage =
    canView && (isHqAdmin || (isFleetOwner && Boolean(operatorId)));
  const gatesLoading = authLoading || fleetLoading || permLoading;

  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<VehicleAvailabilityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState<Dayjs>(() => parseDateParam(searchParams.get('date')));
  const [windowStart, setWindowStart] = useState<Dayjs | null>(null);
  const [windowEnd, setWindowEnd] = useState<Dayjs | null>(null);
  const [statusFilter, setStatusFilter] = useState<VehicleAvailabilityStatus | 'all'>('all');
  const [operatorFilter, setOperatorFilter] = useState<string | undefined>(undefined);

  const scheduleDate = date.format('YYYY-MM-DD');

  useEffect(() => {
    const fromUrl = searchParams.get('date');
    if (!fromUrl) {
      setSearchParams({ date: scheduleDate }, { replace: true });
      return;
    }
    if (dayjs(fromUrl, 'YYYY-MM-DD', true).isValid() && fromUrl !== scheduleDate) {
      setDate(dayjs(fromUrl, 'YYYY-MM-DD'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync URL → state when searchParams change
  }, [searchParams]);

  const setDateAndUrl = (d: Dayjs) => {
    setDate(d);
    const params = new URLSearchParams(searchParams);
    params.set('date', d.format('YYYY-MM-DD'));
    setSearchParams(params, { replace: true });
  };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchVehicleAvailability({
        date: date.format('YYYY-MM-DD'),
        windowStart: windowStart ? windowStart.format('HH:mm:ss') : null,
        windowEnd: windowEnd ? windowEnd.format('HH:mm:ss') : null,
      });
      setRows(data);
    } catch (err) {
      console.error(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [date, windowStart, windowEnd]);

  useEffect(() => {
    if (gatesLoading || !canAccessPage) return;
    void fetchRows();
  }, [gatesLoading, canAccessPage, fetchRows]);

  const operators = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rows) {
      map.set(r.fleet_operator_id, r.operator_name);
    }
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.availability_status !== statusFilter) return false;
      if (isHqAdmin && operatorFilter && r.fleet_operator_id !== operatorFilter) return false;
      return true;
    });
  }, [rows, statusFilter, operatorFilter, isHqAdmin]);

  const summary = useMemo(() => summarizeVehicleAvailability(rows), [rows]);

  if (gatesLoading) {
    return (
      <AdminSectionPage
        title={t('fleetAvailability.title')}
        subtitle={t('fleetAvailability.loadingSubtitle')}
        loading
      />
    );
  }

  if (!canAccessPage) {
    return <ForbiddenPage />;
  }

  const statusOptions: { value: VehicleAvailabilityStatus | 'all'; label: string }[] = [
    { value: 'all', label: t('fleetAvailability.status.all') },
    { value: 'available', label: t('fleetAvailability.status.available') },
    { value: 'busy', label: t('fleetAvailability.status.busy') },
    { value: 'pending_decision', label: t('fleetAvailability.status.pendingDecision') },
    { value: 'maintenance', label: t('fleetAvailability.status.maintenance') },
  ];

  const columns: TableProps<VehicleAvailabilityRow>['columns'] = [
    {
      title: t('fleetAvailability.columns.fleetNumber'),
      dataIndex: 'fleet_number',
      key: 'fleet_number',
      sorter: (a, b) => a.fleet_number.localeCompare(b.fleet_number),
    },
    {
      title: t('fleetAvailability.columns.model'),
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: t('fleetAvailability.columns.capacity'),
      dataIndex: 'capacity',
      key: 'capacity',
      render: (v: number) => formatCapacity(v),
    },
    ...(isHqAdmin
      ? [
          {
            title: t('fleetAvailability.columns.operator'),
            dataIndex: 'operator_name',
            key: 'operator_name',
            sorter: (a: VehicleAvailabilityRow, b: VehicleAvailabilityRow) =>
              a.operator_name.localeCompare(b.operator_name),
          } satisfies NonNullable<TableProps<VehicleAvailabilityRow>['columns']>[number],
        ]
      : []),
    {
      title: t('fleetAvailability.columns.availability'),
      dataIndex: 'availability_status',
      key: 'availability_status',
      render: (status: VehicleAvailabilityStatus) => (
        <StatusTag
          status={status}
          color={getAvailabilityStatusColor(status)}
          label={formatAvailabilityStatusLabel(status)}
        />
      ),
      filters: statusOptions.filter((o) => o.value !== 'all').map((o) => ({
        text: o.label,
        value: o.value,
      })),
      onFilter: (value, record) => record.availability_status === value,
    },
    {
      title: t('fleetAvailability.columns.busyWindow'),
      key: 'busy_window',
      render: (_, r) =>
        r.busy_from && r.busy_until
          ? `${formatTime(r.busy_from)} – ${formatTime(r.busy_until)}`
          : '—',
    },
    {
      title: t('fleetAvailability.columns.tripsToday'),
      dataIndex: 'trip_count',
      key: 'trip_count',
      width: 100,
      sorter: (a, b) => a.trip_count - b.trip_count,
    },
  ];

  const hasActiveFilters =
    statusFilter !== 'all' ||
    (isHqAdmin && !!operatorFilter) ||
    !!windowStart ||
    !!windowEnd;

  return (
    <AdminListPage
      title={t('fleetAvailability.title')}
      subtitle={
        isHqAdmin
          ? t('fleetAvailability.subtitle')
          : t('fleetAvailability.subtitleFleet')
      }
      resultCount={filtered.length}
      filters={
        <>
          <DatePicker value={date} onChange={(d) => d && setDateAndUrl(d)} allowClear={false} />
          <TimePicker
            placeholder={t('fleetAvailability.placeholders.windowFrom')}
            format="HH:mm"
            value={windowStart}
            onChange={setWindowStart}
            allowClear
          />
          <TimePicker
            placeholder={t('fleetAvailability.placeholders.windowTo')}
            format="HH:mm"
            value={windowEnd}
            onChange={setWindowEnd}
            allowClear
          />
          {isHqAdmin ? (
            <Select
              allowClear
              placeholder={t('fleetAvailability.placeholders.operator')}
              style={{ minWidth: 180 }}
              value={operatorFilter}
              onChange={setOperatorFilter}
              options={operators.map((o) => ({ value: o.id, label: o.name }))}
            />
          ) : null}
          <Select
            style={{ minWidth: 160 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusOptions}
          />
        </>
      }
      hasActiveFilters={hasActiveFilters}
      onClearFilters={() => {
        setStatusFilter('all');
        setOperatorFilter(undefined);
        setWindowStart(null);
        setWindowEnd(null);
      }}
      summary={
        <Row gutter={[16, 16]} className="admin-stat-grid">
          <Col xs={12} sm={6}>
            <Card className="admin-inner-card admin-inner-card--compact">
              <Statistic
                title={t('fleetAvailability.status.available')}
                value={summary.available}
                valueStyle={{ color: 'var(--color-accent)' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className="admin-inner-card admin-inner-card--compact">
              <Statistic
                title={t('fleetAvailability.status.busy')}
                value={summary.busy}
                valueStyle={{ color: 'var(--color-primary)' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className="admin-inner-card admin-inner-card--compact">
              <Statistic
                title={t('fleetAvailability.status.pendingDecision')}
                value={summary.pending_decision}
                valueStyle={{ color: 'var(--color-warning)' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className="admin-inner-card admin-inner-card--compact">
              <Statistic title={t('fleetAvailability.status.maintenance')} value={summary.maintenance} />
            </Card>
          </Col>
        </Row>
      }
      alerts={
        <Text type="secondary">{t('fleetAvailability.alert')}</Text>
      }
    >
      <Table
        dataSource={filtered}
        columns={columns}
        loading={loading}
        rowKey="vehicle_id"
        pagination={DEFAULT_TABLE_PAGINATION}
        scroll={{ x: 900 }}
        locale={{ emptyText: t('fleetAvailability.empty') }}
      />
    </AdminListPage>
  );
}

export default FleetAvailability;
